import { Table } from '@tanstack/react-table';

import { Button } from '@components/button';
import { IconDownload } from '@components/icons/download';
import { downloadAsCsv } from '@utils/browser';

import { accessorMap } from './renderers';
import { sortByReferenceArray } from './sorters';
import { AvailableColumn, ColumnType, DataItemType } from './types';

export const ExportCSV = <T extends DataItemType>({
  availableColumns,
  table,
}: {
  availableColumns: Array<AvailableColumn<T>>;
  table: Table<T>;
}) => {
  return (
    <Button
      size="sm"
      iconLeft={<IconDownload />}
      variant="ghost"
      text="Export"
      onClick={() => {
        exportTableData(availableColumns, table);
      }}
    />
  );
};

export const DANGEROUS_START_CHARACTERS = ['=', '+', '-', '@'];

const exportTableData = <T extends DataItemType>(
  availableColumns: Array<AvailableColumn<T>>,
  table: Table<any>
) => {
  const tableState = table.getState();
  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleFlatColumns().map((column) => column.id);
  const orderedVisibleIds = sortByReferenceArray(visibleColumns, tableState.columnOrder);
  const orderedColumns = orderedVisibleIds
    .map((id) => availableColumns.find((column) => column.id === id))
    .filter(Boolean) as AvailableColumn<T>[];

  const headerRow = orderedColumns.map((column) => column.title).join(',');

  const sheetRows = [
    headerRow,
    ...rows.map((row) => {
      return orderedColumns
        .map((column) => {
          const getValue = column.extractValue
            ? column.extractValue
            : accessorMap[column.type as ColumnType] ?? accessorMap.text;
          const rowValue = getValue(row.original[column.id], column);

          return typeof rowValue === 'string'
            ? `"${
                DANGEROUS_START_CHARACTERS.some((char) => rowValue.startsWith(char))
                  ? `'${rowValue}`
                  : rowValue
              }"`
            : rowValue;
        })
        .join(',');
    }),
  ];
  const csvContent = sheetRows.join('\n');
  downloadAsCsv({ content: csvContent });
};
